import { twMerge } from '@shared/themes';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import type { ComponentProps } from 'react';
import { AppDownloadLinks } from '../AppDownloadLinks/AppDownloadLinks';
import { DecorationShine } from '../DecorationShine';

type AppDownloadSectionProps = ComponentProps<'section'> & {
  namespace: string;
};

/**
 * Section for app download with responsive background images.
 * Uses translations from the provided namespace for title and description.
 * Background images are served from /images/app-download/ directory.
 *
 * @param namespace - The translation namespace to use (e.g., 'home.app_download')
 */
function AppDownloadSection({ namespace, className, ...props }: AppDownloadSectionProps) {
  const t = useTranslations(namespace);

  return (
    <section
      className={twMerge('ll-grid-container w-full dark relative py-8 md:py-20', className)}
      data-testid="app-download-section"
      data-track-container="Discover Apps"
      {...props}
    >
      <Image
        src="/images/app-download/AppDownloadBackgroundSmall.png"
        alt="App download background"
        fill
        className="object-fill object-center ll-container-full block md:hidden"
        priority
      />
      <Image
        src="/images/app-download/AppDownloadBackgroundBig.png"
        alt="App download background"
        fill
        className="object-fill object-center ll-container-full hidden md:block"
        priority
      />
      <div className="flex flex-col items-center justify-center text-center h-full relative z-10 ll-container-inner px-4">
        <DecorationShine className="size-10 mb-6 text-white md:hidden" data-testid="app-download-shine-icon" />
        <h2 className="display-s md:display-m mb-5" data-testid="app-download-title">
          {t('title')}
        </h2>
        <p className="paragraph mb-10" data-testid="app-download-description">
          {t('description')}
        </p>
        <AppDownloadLinks className="mb-37 md:mb-5" />
      </div>
    </section>
  );
}

export { AppDownloadSection, type AppDownloadSectionProps };
